// **Author:** Vijay 
import React from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button, Box, Typography, Container, Paper, Avatar, Grid } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Input from "../../components/Input";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import httpClient from "../../services/index.js";

const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  password: yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
  initialBalance: yup
    .number()
    .typeError("Initial Balance must be a number")
    .min(0, "Initial Balance must be at least 0")
    .required("Initial Balance is required"),
});

export default function Register() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      initialBalance: 0,
    },
    mode: "onChange",
  });

  const [loading, setLoading] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [successMsg, setSuccessMsg] = React.useState("");
  const [accountType, setAccountType] = React.useState("Savings");
  // Optionally, let user input account number, otherwise generate
  // const [accountNumber, setAccountNumber] = React.useState('');
  const navigate = useNavigate();

  // Helper to generate a random account number
  const generateAccountNumber = () => "AC" + Math.floor(100000000 + Math.random() * 900000000);

  const onSubmit = async (data) => {
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const accountNumber = generateAccountNumber();
      const accountCreationDate = new Date().toISOString();
      const payload = {
        name: data.name,
        email: data.email,
        password: data.password,
        accountType,
        accountNumber,
        availableBalance: data.initialBalance,
        accountCreationDate,
        transactions: [],
      };
      const res = await httpClient.post("/customers", payload);
      if (res.success) {
        setSuccessMsg("Registration successful!");
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      } else {
        setErrorMsg(res.error?.message || "Registration failed");
      }
    } catch (err) {
      setErrorMsg("Server error. Please try again later.");
    }
    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #6B73FF 0%, #000DFF 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Container maxWidth="xs">
        <Paper elevation={6} sx={{ p: 4, borderRadius: 4 }}>
          <Grid container direction="column" alignItems="center" spacing={2}>
            <Grid item>
              <Avatar sx={{ bgcolor: "#000DFF", width: 56, height: 56 }}>
                <LockOutlinedIcon fontSize="large" />
              </Avatar>
            </Grid>
            <Grid item>
              <Typography variant="h5" fontWeight={700} color="#222" align="center">
                Create Account
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                Register as a customer
              </Typography>
            </Grid>
            <Grid item sx={{ width: "100%" }}>
              <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                {errorMsg && (
                  <Typography color="error" align="center" sx={{ mb: 2 }}>
                    {errorMsg}
                  </Typography>
                )}
                {successMsg && (
                  <Typography color="success.main" align="center" sx={{ mb: 2 }}>
                    {successMsg}
                  </Typography>
                )}
                <Input label="Name" type="text" register={register("name")} error={errors.name?.message} fullWidth />
                <Input
                  label="Email"
                  type="email"
                  register={register("email")}
                  error={errors.email?.message}
                  fullWidth
                />
                <Input
                  label="Password"
                  type="password"
                  register={register("password")}
                  error={errors.password?.message}
                  fullWidth
                />
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id="account-type-label">Account Type</InputLabel>
                  <Select
                    labelId="account-type-label"
                    id="account-type"
                    value={accountType}
                    label="Account Type"
                    onChange={(e) => setAccountType(e.target.value)}
                  >
                    <MenuItem value="Savings">Savings</MenuItem>
                    <MenuItem value="Current">Current</MenuItem>
                  </Select>
                </FormControl>
                {/* Initial Balance field removed from UI, but still validated and used in payload */}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 3, py: 1.5, fontWeight: 600, fontSize: "1rem", borderRadius: 2 }}
                >
                  Register
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}
